import React, { memo } from 'react';
import cls from 'classnames';

import { dataLocators } from './constants';
import { ServerCard, Card, FieldsValues } from './types';
import { CreditCardContext, useCreditCardContext } from './provider';

import { CardNumber } from './blocks/Controls/CardNumber';
import { CardHolderInput } from './blocks/Controls/CardHolderInput';
import { CVVInput } from './blocks/Controls/CVVInput';
import { DatesInput } from './blocks/Controls/DatesInput';
import { SaveCardCheckbox } from './blocks/Controls/SaveCardCheckbox';

import styles from './CreditCard.scss';

export type Props = {
  /** Класс на внешний контейнер */
  className?: string;

  /** Массив сохраненных карт пользователя */
  savedCards?: ServerCard[];

  /** Новая или выбранная сохраненная карта */
  activeCard: Card;

  /** Колбек для установки значения карты */
  setActiveCard: (values: Card) => void;

  /* Объект ошибок для подсветки инпутов */
  errors?: Partial<Record<FieldsValues, string>>;

  /** Состояние формы. Нужно для отображения ошибки, даже если не изменяли поле */
  isSubmitted?: boolean;

  /** Нужно ли прятать поле cardHolder */
  isCardHolderHidden?: boolean;
};

type ComponentProps = Pick<Props, 'className' | 'isCardHolderHidden'>;

export const CreditCardComponent = memo((props: ComponentProps) => {
  const { className, isCardHolderHidden }: ComponentProps = props;

  const { isSavedCard, creditCardRef } = useCreditCardContext();

  return (
    <div
      className={cls(styles.CreditCard, className)}
      ref={creditCardRef}
      data-locator={dataLocators.root}
    >
      <div className={styles.container}>
        <CardNumber />

        <div className={styles.details}>
          <DatesInput />
          <CVVInput />
        </div>

        {!isCardHolderHidden && <CardHolderInput />}
      </div>
      {!isSavedCard && <SaveCardCheckbox />}
    </div>
  );
});

export const CreditCard = (props: Props) => {
  const {
    activeCard,
    setActiveCard,
    errors = {},
    savedCards = [],
    isSubmitted = false,
    className = '',
    isCardHolderHidden = false,
  } = props;

  return (
    <CreditCardContext
      activeCard={activeCard}
      setActiveCard={setActiveCard}
      errors={errors}
      savedCards={savedCards}
      isSubmitted={isSubmitted}
    >
      <CreditCardComponent className={className} isCardHolderHidden={isCardHolderHidden} />
    </CreditCardContext>
  );
};
